/**
 * ============================================================================
 * USE STORAGE HOOK
 * ============================================================================
 * 
 * Custom hook for file storage operations
 */

import { useState, useCallback } from 'react';
import * as storageService from '../services/storage';

export const useStorage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  /**
   * Upload file
   */
  const uploadFile = useCallback(async (file, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', file);
      
      if (options.folder) {
        formData.append('folder', options.folder);
      }
      if (options.public) {
        formData.append('public', options.public);
      }
      if (options.metadata) {
        formData.append('metadata', JSON.stringify(options.metadata));
      }

      const response = await storageService.uploadFile(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });
      
      setUploadProgress(100);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to upload file');
      throw err;
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, []);

  /**
   * Get signed URL
   */
  const getSignedUrl = useCallback(async (filePath, expiresIn = 3600) => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.getSignedUrl(filePath, expiresIn);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get signed URL');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get file metadata
   */
  const getFileMetadata = useCallback(async (filePath) => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.getFileMetadata(filePath);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get file metadata');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete file
   */
  const deleteFile = useCallback(async (filePath) => {
    try {
      setLoading(true);
      setError(null);
      await storageService.deleteFile(filePath);
    } catch (err) {
      setError(err.message || 'Failed to delete file');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate image preview URL
   */
  const generateImagePreview = useCallback(async (filePath, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.generateImagePreviewUrl(filePath, options);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to generate image preview');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Generate PDF preview URL
   */
  const generatePDFPreview = useCallback(async (filePath, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.generatePDFPreviewUrl(filePath, options);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to generate PDF preview');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if file exists
   */
  const checkFileExists = useCallback(async (filePath) => {
    try {
      const response = await storageService.fileExists(filePath);
      return response.data?.exists || false;
    } catch (err) {
      console.error('Failed to check file existence:', err);
      return false;
    }
  }, []);

  /**
   * Upload multiple files
   */
  const uploadMultipleFiles = useCallback(async (files, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      setUploadProgress(0);
      
      const uploadPromises = files.map((file, index) => 
        uploadFile(file, { ...options, index })
      );
      
      const results = await Promise.all(uploadPromises);
      setUploadProgress(100);
      return results;
    } catch (err) {
      setError(err.message || 'Failed to upload files');
      throw err;
    } finally {
      setLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, [uploadFile]);

  /**
   * Download file
   */
  const downloadFile = useCallback(async (filePath, filename = null) => {
    try {
      setLoading(true);
      setError(null);
      const signedUrl = await getSignedUrl(filePath);
      
      const link = document.createElement('a');
      link.href = signedUrl.url || signedUrl;
      link.download = filename || filePath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message || 'Failed to download file');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getSignedUrl]);

  /**
   * List files
   */
  const listFiles = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.listFiles(options);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to list files');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get storage quota
   */
  const getStorageQuota = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storageService.getStorageQuota();
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to get storage quota');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    uploadProgress,
    loading,
    error,
    files: null, // Will be set by component state
    quota: null, // Will be set by component state
    
    // Actions
    uploadFile,
    uploadMultipleFiles,
    getSignedUrl,
    getFileMetadata,
    deleteFile,
    generateImagePreview,
    generatePDFPreview,
    checkFileExists,
    downloadFile,
    listFiles,
    getStorageQuota,
  };
};

export default useStorage;
